import { Button, ButtonProps, styled } from '@mui/material';
import DESIGN_COLORS from 'shared/_ui/design-colors';
import { IconPlus } from 'shared/_ui/icon';
import FileTypesSelectDialog from 'shared/_model/project-files/FileTypesSelectDialog';
import { ComponentProps, useCallback, useContext, useRef, useState } from 'react';
import { DocTypeFiles } from 'shared/constants/docs';
import { asyncForEach } from 'shared/utils/common';
import { getSelectedFile, TSelectedFile } from 'shared/utils/files/files';
import { useProjectContext } from 'shared/_pages/project/provider';
import { UserDataContext, useUserData } from 'shared/Providers/UserData'
import CopyMoveConfirmDialog from 'shared/_components/copy-move-confirm-dialog'

/** `group` — quiet subsection button, `section` — dark compact section-header button. */
type ButtonVariation = 'top' | 'group' | 'section' | 'regular';
const AddFileButtonStyled = styled(Button, {
	name: 'AddFileButtonStyled',
	shouldForwardProp: (propName: string) => !['variation'].includes(propName),
})<{ variation?: ButtonVariation }>(({ theme, variation }) => {
	const commonSx = {
		minWidth: 'max-content',
	};
	let breakpointsSx = {};
	if (variation === 'top') {
		breakpointsSx = {
			[theme.breakpoints.down('md')]: {
				position: 'fixed',
				bottom: theme.spacing(8),
				right: theme.spacing(8),
				left: `calc(158px + ${theme.spacing(8)})`, // 158px - nav width
				width: `calc(100% - ${theme.spacing(16)} - 158px)`,
				minWidth: 'max-content',
			},
			[theme.breakpoints.up('md')]: {
				fontSize: '1.25rem',
				lineHeight: 1.4,
				fontWeight: 700,
				width: '7.5rem',
				height: '4rem',
			},
		};
	}

	if (variation === 'group' || variation === 'section') {
		const isSection = variation === 'section';
		return {
			...commonSx,
			gap: theme.spacing(isSection ? 1.5 : 1.25),
			padding: isSection ? theme.spacing(1.75, 3.25) : theme.spacing(1.25, 2.25),
			borderRadius: isSection ? theme.spacing(2) : '0.375rem',
			fontSize: isSection ? '0.8125rem' : '0.78rem',
			fontWeight: 700,
			lineHeight: 'normal',
			textTransform: 'none' as const,
			...(!isSection && { color: DESIGN_COLORS.muted }),
			'.MuiButton-startIcon': {
				margin: 0,
				'&>*:nth-of-type(1)': { fontSize: '0.8125rem' },
			},
			'&:hover': isSection
				? { backgroundColor: DESIGN_COLORS.dark2 }
				: { backgroundColor: DESIGN_COLORS.borderSoft, color: DESIGN_COLORS.strong },
		};
	}

	return {
		...commonSx,
		...breakpointsSx,
	};
});

type PropsButton = {
	buttonProps?: ButtonProps;
	/** Button text, "Add" by default. */
	label?: string;
	disabled?: boolean;
	type?: DocTypeFiles;
	variation?: ButtonVariation;
};
type PropsNewProject = {
	askForFileOperation: false;
	addFiles?: (files: TSelectedFile[]) => Promise<unknown> | unknown;
};
type PropsExistingProject = {
	askForFileOperation: true;
	addFiles?: (files: TSelectedFile[], method: 'copy' | 'move') => Promise<unknown> | unknown;
};
type Props = PropsButton & (PropsNewProject | PropsExistingProject);

function AddFileButton({ type: typeProp, buttonProps, addFiles, askForFileOperation, label = 'Add', ...rest }: Props) {
	const typeRef = useRef(typeProp);
	// const { addFilesNew } = useProjectContext();
	const [showAddFileDialog, setShowAddFileDialog] = useState(false);
	const [showCopyMoveConfirm, setShowCopyMoveConfirm] = useState(false);
	const {
		user: {
			settings: { filesCopyMethod },
		},
	} = useUserData();
	const { updateUser } = useContext(UserDataContext);
	const filesRef = useRef<TSelectedFile[]>([]);
	const inputRef = useRef<HTMLInputElement>(null);

	const validate = useCallback(
		async (files: TSelectedFile[]) => {
			if (filesCopyMethod || !askForFileOperation) {
				await addFiles?.(files, filesCopyMethod || 'copy');
				return;
			}
			filesRef.current = files;
			setShowCopyMoveConfirm(true);
		},
		[filesCopyMethod, addFiles, askForFileOperation]
	);

	const handleInputChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const files = await asyncForEach([...(e.target.files ?? [])], async (file) => getSelectedFile(file, typeRef.current!));
			validate(files);
		},
		[typeRef, validate],
	);

	const handleAddFile = useCallback(() => {
		if (inputRef.current) {
			inputRef.current.click();
		}
	}, []);

	const handleAddFileClick = useCallback(() => {
		if (typeRef.current) {
			handleAddFile();
		} else {
			setShowAddFileDialog(true);
		}
	}, [handleAddFile, typeRef]);

	const handleFileTypeClick = useCallback(
		(selectedType?: DocTypeFiles) => {
			setShowAddFileDialog(false);
			if (selectedType) {
				typeRef.current = selectedType;
				handleAddFile();
			}
		},
		[handleAddFile],
	);

	const handleConfirmSubmit = useCallback<ComponentProps<typeof CopyMoveConfirmDialog>['onChange']>(
		async (method, save) => {
			if (save) {
				await updateUser({ settings: { filesCopyMethod: method } });
			}
			setShowCopyMoveConfirm(false);
			await addFiles?.(filesRef.current, method);
			filesRef.current = [];
		},
		[addFiles, updateUser]
	);
	const handleConfirmClose = useCallback(() => {
		setShowCopyMoveConfirm(false);
		filesRef.current = [];
	}, []);

	const isCompact = rest.variation === 'group' || rest.variation === 'section';

	return (
		<>
			<AddFileButtonStyled
				{...buttonProps}
				{...rest}
				{...(rest.variation === 'group' && { variant: 'text' as const })}
				{...(isCompact ? { startIcon: <IconPlus /> } : { endIcon: <IconPlus /> })}
				onClick={handleAddFileClick}
				// size="small"
			>
				{label}
			</AddFileButtonStyled>
			{showAddFileDialog && <FileTypesSelectDialog onClose={handleFileTypeClick} />}
			{showCopyMoveConfirm && (
				<CopyMoveConfirmDialog onChange={handleConfirmSubmit} onClose={handleConfirmClose} />
			)}
			<input ref={inputRef} onChange={handleInputChange} type="file" multiple style={{ display: 'none' }} />
		</>
	);
}

export function AddFileToProjectButton(props: PropsButton) {
	const { addFilesNew } = useProjectContext();
	return <AddFileButton {...props} askForFileOperation addFiles={addFilesNew} />;
}

export function AddFileToNewProjectButton(props: PropsButton & Pick<PropsNewProject, 'addFiles'>) {
	return <AddFileButton {...props} askForFileOperation={false} />;
}
